import { FC, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQuery, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'

import QueueNumbersAPI, { PayloadCreateQueueNumber } from '@api/queueNumbersAPI'
import { QueuesAPI } from '@api/queuesAPI'

import { Button } from '@components/Button'
import { FixedDraggable, FixedDraggableProvider } from '@components/FixedDraggable'

import { PermissionsControl } from '@modules/permissions'
import { QueueMenuContext } from '@modules/queues/contexts/QueueMenuContext'

import { PermissionEnum } from '@/constants/permission'
import { QUERIES } from '@/constants/queries'
import FormUtil from '@/utils/formUtil'

import { QueueCheckinCounterForm } from '../QueueCheckinCounterForm/QueueCheckinCounterForm'
import { QueueCounterCheckoutButton } from '../QueueCounterCheckoutButton'
import { QueueNumberFormModal, QueueNumberFormShape } from '../QueueNumberFormModal'
import { QueueProcessTopbar } from '../QueueProcessTopbar/QueueProcessTopbar'
import { QueueTopbarMenuButton } from '../QueueTopbarMenuButton'

const QueueTopbarMenu: FC = () => {
  const intl = useIntl()
  const client = useQueryClient()
  const [isShowCreateModal, setIsShowCreateModal] = useState<boolean>(false)

  const { data, error, refetch } = useQuery({
    queryKey: [QUERIES.QUEUE_PROCESS, 'check-assigned'],
    queryFn: () => QueuesAPI.process(),
    select: (response) => response.data?.queue_process,
    keepPreviousData: false,
    retry: 0,
    cacheTime: 0,
  })

  const contextValue = {
    queueProcess: data,
    refetchQueueProcess: refetch,
  }

  const handleCreateQueueNumber = async (values: QueueNumberFormShape) => {
    const payload = FormUtil.parseValues<PayloadCreateQueueNumber>(values)
    await QueueNumbersAPI.create(payload)
    client.invalidateQueries(QUERIES.QUEUE_PROCESS)
    setIsShowCreateModal(false)
  }

  return (
    <QueueMenuContext.Provider value={contextValue}>
      <FixedDraggableProvider>
        <QueueTopbarMenuButton />

        <FixedDraggable title={intl.formatMessage({ id: 'queue.header.title' })}>
          {!error && data ? (
            <div>
              <PermissionsControl allow={PermissionEnum.QUEUE_NUMBER_GENERATE}>
                <div className="mb-5">
                  <Button
                    theme="primary"
                    className="w-100"
                    onClick={() => setIsShowCreateModal(true)}
                  >
                    {intl.formatMessage({ id: 'queue_number.generate' })}
                  </Button>

                  <QueueNumberFormModal
                    isGenerate
                    isShow={isShowCreateModal}
                    currentQueue={{
                      id: data.id,
                      name: data.name,
                      allow_multiple_process: data.allow_multiple_process,
                    }}
                    onClose={() => setIsShowCreateModal(false)}
                    onSubmit={handleCreateQueueNumber}
                  />
                </div>
              </PermissionsControl>

              <QueueProcessTopbar />

              <div className="d-flex gap-3 mt-5">
                {/* begin::show detail queue */}
                <PermissionsControl allow={PermissionEnum.QUEUE_SHOW}>
                  <Link
                    className="btn btn-sm btn-outline btn-active-light-primary flex-equal"
                    to={`/queues/overview/${data.id}`}
                  >
                    <i className="fa-solid fa-eye" />
                    <span className="ms-1">{intl.formatMessage({ id: 'view.all' })}</span>
                  </Link>
                </PermissionsControl>
                {/* end::show detail queue */}

                {/* begin::check out queue */}
                <PermissionsControl allow={PermissionEnum.QUEUE_COUNTER_CHECK_OUT}>
                  <QueueCounterCheckoutButton />
                </PermissionsControl>
                {/* begin::check out queue */}
              </div>
            </div>
          ) : (
            <QueueCheckinCounterForm />
          )}
        </FixedDraggable>
      </FixedDraggableProvider>
    </QueueMenuContext.Provider>
  )
}

export { QueueTopbarMenu }
